import { copyConstants } from '@starlingbank/shared';

export const businessCompareTitles = {
  'Original free account': {
    type: 'free',
    amount: '0',
    className: 'sme-business-toolkit__col sme-business-toolkit__col--thin sme-business-toolkit__col--border',
    new: false,
    vat: false,
    link: '#apply',
    linkText: 'Apply now'
  },
  'Add-on the Business Toolkit': {
    type: 'toolkit',
    amount: '7',
    className: 'sme-business-toolkit__col sme-business-toolkit__col--thin sme-business-toolkit__col--grey',
    vat: true,
    link: '/business-account/sme-business-toolkit/',
    linkText: 'Explore the Toolkit'
  }
};

export const businessCompareSubHeadings = {
  'Businesses supported': {
    sole: {
      name: '<a href="/sole-trader-bank-account/">Sole traders</a>',
      bio: 'When applying for the account we will need evidence of trading activity, and the sole trader can’t engage in any of the prohibited activities set out in the Sole Trader Current Account Terms'
    },
    ltd: {
      name: 'Limited Companies and Limited Liability Partnerships (including startups)'
    },
    psc: {
      name: 'Companies with multiple PSCs / Directors',
      bio: 'Multiple Directors (registered at Companies House) can access the account, but we can’t yet provide access to third parties (e.g. accountants) or other team members (e.g. finance teams or assistants)'
    },
    client: {
      name: 'Client money accounts or the accounts of businesses regulated by the FCA not supported'
    }
  },
  'Starling’s best-loved': {
    regulated: {
      name: 'Free UK current account from a regulated bank',
      bio: 'No monthly fees on opening a business account. Starling Bank is a fully-licensed bank. You can add-on the Toolkit for £7 per month (inc VAT).'
    },
    apply: {
      name: 'Easy in-app application',
      bio: 'Applying for a business account can be done from your mobile phone.'
    },
    fscs: {
      name: '<a href="/legal/account/fscs-protection/" target="_blank">FSCS Protection</a> up to £85,000',
      bio: 'All of Starling’s eligible accounts are protected up to £85,000 by the Financial Services Compensation Scheme.'
    },
    mobile: {
      name: 'Mobile and online banking',
      bio: 'Access and manage your business account from the app or through online banking.'
    },
    customer: {
      name: '24/7 customer support',
      bio: 'Free 24/7 UK support via app, phone or email, with fast response times and help from real humans.'
    },
    atm: {
      name: 'Free ATM withdrawals',
      bio: 'Starling will not charge a fee when you withdraw money from an ATM. You can make up to six withdrawals per day, with a daily limit of £300, regardless of what currency you are withdrawing.'
    },
    abroad: {
      name: 'No fees abroad',
      bio: 'Starling will not charge you for card payments or ATM withdrawals when you’re abroad.'
    },
    receipt: {
      name: 'Receipt capture',
      bio: 'Keep your files in order by adding digital receipts to payments.'
    },
    saving: {
      name: '<a href="/features/spaces/">Spaces</a>',
      bio: 'Separate spaces for separate costs. Set things like tax and overheads to one side.'
    },
    security: {
      name: 'Card security controls',
      bio: 'In-app card locking and personalised card settings.'
    }
  },
  'Good for business': {
    psc: {
      name: '<a href="https://help.starlingbank.com/hc/en-us/articles/360017795360-How-do-I-add-change-a-Director-or-Person-of-Significant-Control-PSC-on-my-Business-Current-Account-" target="_blank" rel="noopener noreferrer">Free access to multiple directors</a>',
      bio: 'Directors must be registered at companies house.'
    },
    xero: {
      name: 'Integration with <a href="/marketplace/partners/xero/">Xero</a>',
      bio: 'From the Starling Marketplace, you can integrate Xero with your business account.'
    },
    quickbooks: {
      name: 'Integration with QuickBooks',
      bio: 'From the Starling Marketplace, you can integrate QuickBooks with your business account.'
    },
    freeagent: {
      name: 'Integration with FreeAgent',
      bio: 'From the Starling Marketplace, you can integrate FreeAgent with your business account.'
    },
    marketplace: {
      name: '<a href="/business-account/marketplace-for-businesses/">Integrate with other business services</a>',
      bio: 'Connect to some of the world’s best business products and services to create your own in-app financial hub.'
    },
    categories: {
      name: 'Automated business spending categorisation',
      bio: 'Get a breakdown of your company’s spending, organise your outgoings and store receipts.'
    }
  },
  'Designed for bookkeeping': {
    invoices: {
      name: 'Invoices and auto-matching',
      bio: 'Create and manage unlimited invoices directly from your desktop. When you get paid, just match your invoices in a single click.'
    },
    email: {
      name: 'Email integrations',
      bio: 'Link your Google, Outlook or Hotmail email to the Business Toolkit to issue invoices directly from your bank account.'
    },
    tax: {
      name: 'HMRC Tax estimation (sole trader only)',
      bio: 'Estimate your self-employed tax (SA103F), national insurance and student loan liabilities.'
    },
    vatrecord: {
      name: 'Record VAT on transactions',
      bio: 'Record your VAT, and we’ll calculate your VAT return if you’re on cash accounting or flat rate (cash basis).'
    },
    vatsubmit: {
      name: 'MTD VAT submission',
      bio: 'Connect your account to HMRC and submit your VAT return through Making Tax Digital.'
    },
    bookkeeping: {
      name: 'Bookkeeping to-do list',
      bio: 'See what needs to be done in a handy interactive to-do list, so you can stay on top of your bookkeeping admin.'
    },
    bills: {
      name: 'Manage bills',
      bio: 'Upload bills, schedule payments and track your expenses.'
    }
  },
  'Made for payments': {
    transfers: {
      name: 'Free UK bank transfers',
      bio: 'No payment charges on UK bank transfers. You can make a domestic bank transfer (we are part of the UK’s Faster Payments network) of up to £25,000 per transaction in your app / online banking, and an international bank transfer of up to £10,000 per transaction.'
    },
    international: {
      name: 'International payments',
      bio: `Fast and secure money transfers to bank accounts in ${copyConstants.sendMoneyCountryNumber} countries worldwide, with no hidden fees.`
    },
    scheduled: {
      name: 'Managed scheduled payments',
      bio: 'See all your scheduled payments in one place and get notified when they’re due to go out.'
    },
    wallet: {
      name: 'Apple, Google, Samsung and Garmin Pay',
      bio: 'Pay on the go with Apple, Google, Samsung and Google Pay.'
    },
    cheque: {
      name: 'Mobile and Freepost <a href="/features/deposit-cheques-from-mobile/">cheque deposits</a>',
      bio: 'Deposit cheques up to £500 digitally in-app, for free. Cheques above £500 can be sent to us via Freepost. We don’t issue chequebooks. '
    },
    cash: {
      name: 'Cash deposits at the <a href="/features/post-office-branch-finder/">Post Office</a>',
      bio: 'Deposit cash at the Post Office for a 0.3% fee (£3 minimum fee).'
    },
    instant: {
      name: 'Instant payment notifications',
      bio: 'Get notified instantly when you pay or get paid, and get a reminder the day before payments are due.'
    }
  },
  'Help with cashflow': {
    overdraft: {
      name: 'CBILS overdraft',
      bio: 'Get access to government backed overdrafts with CBILS. We provide access to CBILS overdraft finance from £50,001 up to £150,000 for limited companies and Limited Liability Partnerships.'
    },
    loan: {
      name: 'CBILS and BBLS loans',
      bio: 'Get access to government backed BBLS and CBILS loans. For eligible businesses, we provide loans of £2,000 to £50,000 with BBLS, and loans of £50,001 to £250,000 with CBILS.'
    }
  },
  'For going international': {
    usd: {
      name: '<a href="/business-account/multi-currency/usd-business-account/">USD business account</a> - £5 p/month',
      bio: 'Hold, send and receive dollars, all for just £5 per month. Exchange currency at the current exchange rate, with only a 0.4% commission. (note: Our Toolkit add-on only supports GBP accounts).'
    },
    euro: {
      name: '<a href="/business-account/multi-currency/euro-business-account/">Euro business account</a> - £2 p/month)',
      bio: 'Hold, send and receive euro payments, all for just £2 a month.'
    }
  }
};

export const businessCompareData = {
  toolkit: {
    sole: true,
    ltd: true,
    psc: true,
    client: false,
    regulated: true,
    apply: true,
    fscs: true,
    mobile: true,
    customer: true,
    atm: true,
    abroad: true,
    categories: true,
    receipt: true,
    saving: true,
    security: true,
    marketplace: true,
    software: true,
    xero: true,
    quickbooks: true,
    freeagent: true,
    invoices: true,
    email: true,
    tax: true,
    vatrecord: true,
    smarttax: true,
    vatsubmit: true,
    bookkeeping: true,
    bills: true,
    transfers: true,
    international: true,
    scheduled: true,
    wallet: true,
    cheque: true,
    cash: true,
    instant: true,
    overdraft: true,
    loan: true,
    usd: true,
    euro: true
  },
  free: {
    sole: true,
    ltd: true,
    psc: true,
    client: false,
    regulated: true,
    apply: true,
    fscs: true,
    mobile: true,
    customer: true,
    atm: true,
    abroad: true,
    categories: true,
    receipt: true,
    saving: true,
    security: true,
    marketplace: true,
    software: true,
    xero: true,
    quickbooks: true,
    freeagent: true,
    invoices: false,
    email: false,
    tax: false,
    vatrecord: false,
    smarttax: false,
    vatsubmit: false,
    bookkeeping: false,
    bills: false,
    transfers: true,
    international: true,
    scheduled: true,
    wallet: true,
    cheque: true,
    cash: true,
    instant: true,
    overdraft: true,
    loan: true,
    usd: true,
    euro: true
  }
};
